import { useMutation } from "@tanstack/react-query";

import { pendoSignOut } from "@newsela/snitch";
import { useCallback } from "react";
import { useRouter } from "next/router";
import api from "@/api";
import getQueryClient from "@/utils/getQueryClient";
import isRunningOnClient from "@/utils/isRunningOnClient";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import { stripRedirectQueryParams } from "@/utils/appendTrackingParamsToUrl";
import redirectTo from "@/utils/redirectTo";

const useSignOut = () => {
  const { asPath: currentPath } = useRouter();
  const { isImpersonated } = useGetUserProperties();
  const queryClient = getQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const doLogout = async () => {
        await api().delete("/api/v2/session/");
        queryClient.clear();
        queryClient.removeQueries({ queryKey: ["user"] });
        pendoSignOut();
      };

      return await doLogout();
    },
    retry: 3,
  });

  const memoizedLogout = useCallback(
    async (withNext = true) => {
      if (isRunningOnClient() && isImpersonated) {
        // Redirects the user to the Special Django URL that will stop impersonating
        // and also redirects the admin to the last viewed page
        redirectTo("/impersonate-stop");
        return;
      }

      // Clear up the user session on the backend, calling /api/v2/session/ DELETE
      await mutation.mutateAsync();

      if (withNext) {
        const cleanPath = stripRedirectQueryParams(currentPath);
        const encodedRedirectUrlForNextParam = encodeURIComponent(cleanPath);
        redirectTo(
          `${MONOLITH_ORIGIN}/signin?next=${encodedRedirectUrlForNextParam}`,
        );
      } else {
        redirectTo(`${MONOLITH_ORIGIN}/signin`);
      }
    },
    [mutation],
  );

  return {
    signOut: memoizedLogout,
    error: mutation.error,
    loading: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};

export default useSignOut;
